import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import type { ReactNode } from "react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageSkeleton } from "@/components/Layout"
import {
  useFlashcardDeckCards,
  useTeacherFlashcardDashboard,
} from "@/hooks/useDashboardQueries"
import type {
  FlashcardCardStat,
  FlashcardDeckStat,
  FlashcardProgramSeries,
  FlashcardSubjectStat,
  FlashcardWeeklyPoint,
} from "@/types/dashboard"
import { LuChevronDown, LuChevronUp } from "react-icons/lu"

const SERIES_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"]
const ORG_TOTAL_KEY = "Org total"

interface Props {
  isActive: boolean
  programIds?: string[]
}

interface ChartRow {
  week_start: string
  [seriesName: string]: string | number
}

function buildChartRows(
  orgTotal: FlashcardWeeklyPoint[],
  programSeries: FlashcardProgramSeries[],
): ChartRow[] {
  const visibleSeries = programSeries.filter((s) => s.total_reviews > 0)
  const weeks = orgTotal.map((p) => p.week_start)

  return weeks.map((week, idx) => {
    const row: ChartRow = {
      week_start: week,
      [ORG_TOTAL_KEY]: orgTotal[idx]?.review_count ?? 0,
    }
    for (const s of visibleSeries) {
      row[s.program_name] = s.points[idx]?.review_count ?? 0
    }
    return row
  })
}

function ratingColor(avg: number): string {
  if (avg < 3.0) return "red"
  if (avg < 4.0) return "orange"
  return "green"
}

interface CardDrillDownProps {
  deckId: string
}

function CardDrillDown({ deckId }: CardDrillDownProps) {
  const { data, isLoading, isError } = useFlashcardDeckCards(deckId)

  if (isLoading) {
    return (
      <Box py={4} textAlign="center">
        <Spinner size="sm" />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Text
        color="red.500"
        fontSize="sm"
        py={2}
        data-testid="card-drilldown-error"
      >
        Couldn't load card details.
      </Text>
    )
  }

  if (data.length === 0) {
    return (
      <Text color="fg.muted" fontSize="sm" py={2}>
        No card reviews in this window.
      </Text>
    )
  }

  return (
    <Box data-testid={`card-drilldown-${deckId}`}>
      {/* Desktop */}
      <Box display={{ base: "none", md: "block" }}>
        <Table.Root size="sm" variant="outline" mt={2}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Card (front preview)</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Reviews</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">
                Avg Rating
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.map((card: FlashcardCardStat) => (
              <Table.Row key={card.flashcard_id}>
                <Table.Cell fontSize="xs" color="fg.muted">
                  {card.front_preview}
                </Table.Cell>
                <Table.Cell textAlign="right" fontSize="xs">
                  {card.total_reviews}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <Badge colorPalette={ratingColor(card.avg_rating)} size="sm">
                    {card.avg_rating.toFixed(1)}
                  </Badge>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Mobile cards */}
      <VStack
        display={{ base: "flex", md: "none" }}
        gap={2}
        align="stretch"
        mt={2}
      >
        {data.map((card: FlashcardCardStat) => (
          <Card.Root key={card.flashcard_id} size="sm">
            <Card.Body>
              <Text fontSize="xs" color="fg.muted" lineClamp={2}>
                {card.front_preview}
              </Text>
              <Flex gap={3} mt={2}>
                <Box>
                  <Text fontSize="xs" color="fg.muted">
                    Reviews
                  </Text>
                  <Text fontSize="sm">{card.total_reviews}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="fg.muted">
                    Avg Rating
                  </Text>
                  <Badge colorPalette={ratingColor(card.avg_rating)} size="sm">
                    {card.avg_rating.toFixed(1)}
                  </Badge>
                </Box>
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
    </Box>
  )
}

interface DeckStatsTableProps {
  decks: FlashcardDeckStat[]
  subjectStats?: FlashcardSubjectStat[]
}

interface SubjectDeckGroup {
  key: string
  subject_id: string | null
  subject_name: string
  decks: FlashcardDeckStat[]
}

function subjectKey(subjectId: string | null): string {
  return subjectId ?? "unassigned"
}

function groupDecksBySubject(
  decks: FlashcardDeckStat[],
  subjectStats: FlashcardSubjectStat[] = [],
): SubjectDeckGroup[] {
  const groups = new Map<string, SubjectDeckGroup>()

  for (const stat of subjectStats) {
    const key = subjectKey(stat.subject_id)
    groups.set(key, {
      key,
      subject_id: stat.subject_id,
      subject_name: stat.subject_name,
      decks: [],
    })
  }

  for (const deck of decks) {
    const key = subjectKey(deck.subject_id)
    const group = groups.get(key)
    if (group) {
      group.decks.push(deck)
      continue
    }
    groups.set(key, {
      key,
      subject_id: deck.subject_id,
      subject_name: deck.subject_name || "Unassigned",
      decks: [deck],
    })
  }

  return [...groups.values()].sort((left, right) => {
    if (left.key === "unassigned") return 1
    if (right.key === "unassigned") return -1
    return left.subject_name.localeCompare(right.subject_name)
  })
}

function summarizeDecks(group: SubjectDeckGroup): FlashcardSubjectStat {
  const totalReviews = group.decks.reduce(
    (total, deck) => total + deck.total_reviews,
    0,
  )
  const weightedRating = group.decks.reduce(
    (total, deck) => total + deck.avg_rating * deck.total_reviews,
    0,
  )

  return {
    subject_id: group.subject_id,
    subject_name: group.subject_name,
    total_reviews: totalReviews,
    unique_students: group.decks.reduce(
      (total, deck) => total + deck.unique_students,
      0,
    ),
    avg_rating: totalReviews > 0 ? weightedRating / totalReviews : 0,
  }
}

interface SubjectDeckSectionProps {
  group: SubjectDeckGroup
  summary: FlashcardSubjectStat
  children: ReactNode
}

function SubjectDeckSection({
  group,
  summary,
  children,
}: SubjectDeckSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const panelId = `flashcard-subject-panel-${group.key}`

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderRadius="l2"
      overflow="hidden"
      data-testid={`flashcard-subject-group-${group.key}`}
      data-subject-key={group.key}
      data-expanded={isExpanded ? "true" : "false"}
    >
      <Button
        variant="ghost"
        width="100%"
        height="auto"
        px={4}
        py={3}
        justifyContent="space-between"
        borderRadius="0"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <Flex align="center" gap={3} textAlign="left">
          <Box>
            <Heading size="sm">{group.subject_name}</Heading>
            <Text
              color="fg.muted"
              fontSize="sm"
              fontWeight="normal"
              data-testid={`flashcard-subject-summary-${group.key}`}
            >
              {summary.total_reviews} reviews · {summary.unique_students}{" "}
              students · {summary.avg_rating.toFixed(1)} avg rating
            </Text>
          </Box>
        </Flex>
        {isExpanded ? (
          <LuChevronUp aria-hidden="true" />
        ) : (
          <LuChevronDown aria-hidden="true" />
        )}
      </Button>
      {isExpanded && (
        <Box
          id={panelId}
          role="region"
          borderTopWidth="1px"
          borderColor="border"
          p={4}
        >
          {children}
        </Box>
      )}
    </Box>
  )
}

interface DeckRowsProps {
  decks: FlashcardDeckStat[]
  expandedDeckId: string | null
  onDeckClick: (deckId: string) => void
}

function deckRowKey(deck: FlashcardDeckStat): string {
  return `${deck.deck_id}-${deck.program_id ?? "unassigned"}`
}

function DeckDesktopTable({
  decks,
  expandedDeckId,
  onDeckClick,
}: DeckRowsProps) {
  return (
    <Box display={{ base: "none", md: "block" }}>
      <Table.Root variant="outline" size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Deck</Table.ColumnHeader>
            <Table.ColumnHeader>Program</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Reviews</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Students</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">
              Avg Rating
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {decks.flatMap((deck) => {
            const key = deckRowKey(deck)
            const rows = [
              <Table.Row
                key={key}
                onClick={() => onDeckClick(deck.deck_id)}
                cursor="pointer"
                _hover={{ bg: "bg.subtle" }}
                data-testid={`deck-row-${deck.deck_id}`}
              >
                <Table.Cell fontWeight="medium">{deck.deck_name}</Table.Cell>
                <Table.Cell
                  color={deck.program_id ? "inherit" : "fg.muted"}
                  fontStyle={deck.program_id ? "normal" : "italic"}
                >
                  {deck.program_name}
                </Table.Cell>
                <Table.Cell textAlign="right" fontWeight="semibold">
                  {deck.total_reviews}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {deck.unique_students}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <Badge
                    colorPalette={ratingColor(deck.avg_rating)}
                    size="sm"
                    data-testid={`deck-rating-badge-${deck.deck_id}`}
                    data-rating-color={ratingColor(deck.avg_rating)}
                  >
                    {deck.avg_rating.toFixed(1)}
                  </Badge>
                </Table.Cell>
              </Table.Row>,
            ]
            if (expandedDeckId === deck.deck_id) {
              rows.push(
                <Table.Row key={`${key}-expanded`}>
                  <Table.Cell colSpan={5} bg="bg.subtle" px={8} py={3}>
                    <CardDrillDown deckId={deck.deck_id} />
                  </Table.Cell>
                </Table.Row>,
              )
            }
            return rows
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

function DeckMobileCards({
  decks,
  expandedDeckId,
  onDeckClick,
}: DeckRowsProps) {
  return (
    <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
      {decks.map((deck) => (
        <Card.Root
          key={deckRowKey(deck)}
          onClick={() => onDeckClick(deck.deck_id)}
          cursor="pointer"
          data-testid={`deck-row-${deck.deck_id}`}
        >
          <Card.Body>
            <Text fontWeight="medium">{deck.deck_name}</Text>
            <Text
              fontSize="sm"
              color={deck.program_id ? "inherit" : "fg.muted"}
              fontStyle={deck.program_id ? "normal" : "italic"}
              mt={1}
            >
              {deck.program_name}
            </Text>
            <SimpleGrid columns={3} gap={2} mt={3}>
              <Box>
                <Text fontSize="xs" color="fg.muted">
                  Reviews
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {deck.total_reviews}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="fg.muted">
                  Students
                </Text>
                <Text fontSize="sm">{deck.unique_students}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="fg.muted">
                  Avg Rating
                </Text>
                <Badge
                  colorPalette={ratingColor(deck.avg_rating)}
                  size="sm"
                  data-testid={`deck-rating-badge-${deck.deck_id}`}
                  data-rating-color={ratingColor(deck.avg_rating)}
                >
                  {deck.avg_rating.toFixed(1)}
                </Badge>
              </Box>
            </SimpleGrid>
            {expandedDeckId === deck.deck_id && (
              <Box mt={3} pt={3} borderTopWidth="1px">
                <CardDrillDown deckId={deck.deck_id} />
              </Box>
            )}
          </Card.Body>
        </Card.Root>
      ))}
    </VStack>
  )
}

function DeckStatsTable({ decks, subjectStats = [] }: DeckStatsTableProps) {
  const [expandedDeckId, setExpandedDeckId] = useState<string | null>(null)

  const handleRowClick = (deckId: string) => {
    setExpandedDeckId((prev) => (prev === deckId ? null : deckId))
  }

  const groups = useMemo(
    () => groupDecksBySubject(decks, subjectStats),
    [decks, subjectStats],
  )
  const summaryByKey = useMemo(
    () =>
      new Map(subjectStats.map((stat) => [subjectKey(stat.subject_id), stat])),
    [subjectStats],
  )

  return (
    <Box mt={6} data-testid="deck-stats-table">
      <Heading size="sm" mb={3}>
        Deck Activity by Subject
      </Heading>
      <VStack align="stretch" gap={4}>
        {groups.map((group) => (
          <SubjectDeckSection
            key={group.key}
            group={group}
            summary={summaryByKey.get(group.key) ?? summarizeDecks(group)}
          >
            {group.decks.length === 0 ? (
              <Text color="fg.muted" fontSize="sm">
                No deck activity in this subject.
              </Text>
            ) : (
              <>
                <DeckDesktopTable
                  decks={group.decks}
                  expandedDeckId={expandedDeckId}
                  onDeckClick={handleRowClick}
                />
                <DeckMobileCards
                  decks={group.decks}
                  expandedDeckId={expandedDeckId}
                  onDeckClick={handleRowClick}
                />
              </>
            )}
          </SubjectDeckSection>
        ))}
      </VStack>
      <Text fontSize="xs" color="fg.muted" mt={3}>
        Avg rating: 1–2.9 = hard · 3–3.9 = moderate · 4–5 = good · Last 180 days
      </Text>
    </Box>
  )
}

export default function FlashcardsTab({ isActive, programIds }: Props) {
  const { data, isLoading } = useTeacherFlashcardDashboard({
    enabled: isActive,
    programIds,
  })

  const chartRows = useMemo(() => {
    if (!data) return []
    return buildChartRows(data.org_total_points, data.program_series)
  }, [data])

  const visibleSeriesNames = useMemo(() => {
    if (!data) return []
    return data.program_series
      .filter((s) => s.total_reviews > 0)
      .map((s) => s.program_name)
  }, [data])

  if (isLoading) {
    return <PageSkeleton variant="stats" />
  }

  if (!data) {
    return (
      <AppEmptyState
        title={emptyStateCopy.analytics.noData.title}
        description={emptyStateCopy.analytics.noData.description}
      />
    )
  }

  if (!data.has_any_reviews) {
    return (
      <AppEmptyState
        title={emptyStateCopy.flashcards.noReviews.title}
        description={emptyStateCopy.flashcards.noReviews.description}
        data-testid="flashcard-empty-state"
      />
    )
  }

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={6}>
        <Card.Root p={4}>
          <Stat.Root>
            <Stat.Label>Total reviews (last 6 months)</Stat.Label>
            <Stat.ValueText>{data.total_reviews_in_window}</Stat.ValueText>
          </Stat.Root>
        </Card.Root>
        <Card.Root p={4}>
          <Stat.Root>
            <Stat.Label>Active studiers</Stat.Label>
            <Stat.ValueText>{data.active_studier_count}</Stat.ValueText>
          </Stat.Root>
        </Card.Root>
      </SimpleGrid>

      <Card.Root p={4} data-testid="flashcard-trend-chart">
        <Card.Body>
          <Heading size="sm" mb={4}>
            Weekly review activity
          </Heading>
          <Box height={360}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week_start" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={ORG_TOTAL_KEY}
                  stroke="#4A5568"
                  strokeWidth={3}
                  dot={false}
                />
                {visibleSeriesNames.map((name, idx) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                    strokeWidth={1.5}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card.Body>
      </Card.Root>

      {data.deck_stats.length > 0 && (
        <DeckStatsTable
          decks={data.deck_stats}
          subjectStats={data.subject_stats}
        />
      )}
    </Box>
  )
}
